export type AuthUser = {
  sub: string;
  email: string;
  name: string;
  picture?: string;
};

type TokenSet = {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
};

const TOKEN_KEY = "japa-reader.auth.tokens";
const VERIFIER_KEY = "japa-reader.auth.pkce-verifier";
const STATE_KEY = "japa-reader.auth.state";

const browserOrigin = typeof window === "undefined" ? "http://localhost:3000" : window.location.origin;

const config = {
  apiUrl: (import.meta.env.VITE_API_URL || "").replace(/\/$/, ""),
  domain: (import.meta.env.VITE_COGNITO_DOMAIN || "").replace(/\/$/, ""),
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
  redirectUri: import.meta.env.VITE_COGNITO_REDIRECT_URI || `${browserOrigin}/auth/callback`,
  logoutUri: import.meta.env.VITE_COGNITO_LOGOUT_URI || browserOrigin,
};

export function cloudIsConfigured() {
  return Boolean(config.apiUrl && config.domain && config.clientId);
}

export function apiBaseUrl() {
  return config.apiUrl;
}

export async function initializeAuth(): Promise<AuthUser | null> {
  if (!cloudIsConfigured()) {
    return import.meta.env.DEV
      ? { sub: "local-development", email: "local@japareader.dev", name: "Ambiente local" }
      : null;
  }

  if (window.location.pathname === "/auth/callback") {
    await completeAuthorization();
    window.history.replaceState({}, document.title, "/");
  }

  const tokens = await validTokens();
  return tokens ? userFromIdToken(tokens.idToken) : null;
}

export async function signInWithGoogle() {
  if (!cloudIsConfigured()) throw new Error("A autenticação ainda não foi configurada para este ambiente.");

  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(32);
  const challenge = base64Url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const authorize = new URL(`${config.domain}/oauth2/authorize`);
  authorize.search = new URLSearchParams({
    identity_provider: "Google",
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: challenge,
    state,
  }).toString();
  window.location.assign(authorize.toString());
}

export function signOut() {
  sessionStorage.removeItem(TOKEN_KEY);
  if (!cloudIsConfigured()) {
    window.location.reload();
    return;
  }
  const logout = new URL(`${config.domain}/logout`);
  logout.search = new URLSearchParams({ client_id: config.clientId, logout_uri: config.logoutUri }).toString();
  window.location.assign(logout.toString());
}

export async function getAccessToken() {
  return (await validTokens())?.accessToken ?? "";
}

async function completeAuthorization() {
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get("error_description") || params.get("error");
  if (oauthError) throw new Error(oauthError);

  const code = params.get("code");
  const state = params.get("state");
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!code || !state || !expectedState || state !== expectedState || !verifier) {
    throw new Error("Não foi possível validar o retorno do Google. Tente entrar novamente.");
  }

  const response = await fetch(`${config.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  });
  if (!response.ok) throw new Error("O login foi aceito, mas a sessão não pôde ser criada.");
  const payload = await response.json() as Record<string, unknown>;
  storeTokens(payload);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
}

async function validTokens(): Promise<TokenSet | null> {
  const current = readTokens();
  if (!current) return null;
  if (current.expiresAt > Date.now() + 60_000) return current;
  if (!current.refreshToken) return null;

  const response = await fetch(`${config.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: current.refreshToken,
    }),
  });
  if (!response.ok) {
    sessionStorage.removeItem(TOKEN_KEY);
    return null;
  }
  const payload = await response.json() as Record<string, unknown>;
  return storeTokens({ ...payload, refresh_token: current.refreshToken });
}

function storeTokens(payload: Record<string, unknown>): TokenSet {
  const expiresIn = typeof payload.expires_in === "number" ? payload.expires_in : 3600;
  const tokens: TokenSet = {
    accessToken: String(payload.access_token || ""),
    idToken: String(payload.id_token || ""),
    refreshToken: payload.refresh_token ? String(payload.refresh_token) : undefined,
    expiresAt: Date.now() + expiresIn * 1000,
  };
  if (!tokens.accessToken || !tokens.idToken) throw new Error("O provedor não retornou uma sessão válida.");
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  return tokens;
}

function readTokens(): TokenSet | null {
  try {
    const value = sessionStorage.getItem(TOKEN_KEY);
    return value ? JSON.parse(value) as TokenSet : null;
  } catch {
    sessionStorage.removeItem(TOKEN_KEY);
    return null;
  }
}

function userFromIdToken(token: string): AuthUser {
  const payload = JSON.parse(new TextDecoder().decode(base64UrlBytes(token.split(".")[1] || ""))) as Record<string, unknown>;
  return {
    sub: String(payload.sub || ""),
    email: String(payload.email || ""),
    name: String(payload.name || payload.given_name || payload.email || "Leitor"),
    picture: payload.picture ? String(payload.picture) : undefined,
  };
}

function randomUrlSafe(length: number) {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return base64Url(bytes);
}

function base64Url(value: ArrayBuffer | Uint8Array) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlBytes(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(normalized);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
